import React, { useEffect, useRef } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Carousel as OrcaCarousel } from '@fiverr-private/orca';
import { RouteOptions } from '@fiverr-private/pathfinder';
import { InspireCard } from '../../internal/InspireCard';
import { InspireCarouselCardSkeleton } from '../../internal/InspireCarouselCardSkeleton';
import type { InspireDelivery } from '../../../types/inspire';
import { SLIDES_TO_SHOW, SCROLL_TYPE, BREAKPOINTS } from './constants';
import { getSlidesCountForWidth } from './utils/getSlidesCountForWidth';
import type { CarouselProps } from './types';

// Orca Carousel item props consist of `slides[i]`, `index` & `componentProps` merged:
// https://github.com/fiverr/blocks/blob/9494e51f9e2bff94214e20f4bd0789224d9d403d/packages/orca/src/components/Carousel/index.js#L134
interface CardProps extends InspireDelivery {
  index: number;
  cardHrefRouteOptions?: RouteOptions;
  onHrefClick?: (inspireDelivery: InspireDelivery, index: number) => void;
  onImpression?: (inspireDelivery: InspireDelivery, index: number) => void;
}
const Card = ({ index, onHrefClick, onImpression, cardHrefRouteOptions, ...inspireDelivery }: CardProps) => {
  const { isTouch } = getContext();
  return (
    <InspireCard
      inspireDelivery={inspireDelivery}
      index={index}
      isTouch={isTouch}
      cardHrefRouteOptions={cardHrefRouteOptions}
      variant={InspireCard.VARIANTS.carousel}
      onHrefClick={onHrefClick}
      onImpression={onImpression}
    />
  );
};

export const CarouselSkeleton = () => (
  <OrcaCarousel
    slides={new Array(SLIDES_TO_SHOW).fill(null)}
    slidesToShow={SLIDES_TO_SHOW}
    scrollType={SCROLL_TYPE}
    breakpoints={BREAKPOINTS}
    hideDefaultArrows
    Component={InspireCarouselCardSkeleton}
  />
);

export const Carousel = ({
  inspireDeliveries,
  cardHrefRouteOptions,
  onSeenAll,
  onShowMore,
  onCardHrefClick,
  onCardImpression,
}: CarouselProps) => {
  const seenAllRef = useRef(false);
  const slidesCount = inspireDeliveries.length;

  useEffect(() => {
    if (seenAllRef.current) {
      return;
    }

    const width = window.innerWidth;
    const carouselSlidesCount = getSlidesCountForWidth(width);
    if (slidesCount <= carouselSlidesCount) {
      seenAllRef.current = true;
      if (onSeenAll) {
        onSeenAll();
      }
    }
  }, [slidesCount, onSeenAll]);

  const onScroll = ({ canScrollRight }) => {
    if (!seenAllRef.current && !canScrollRight) {
      seenAllRef.current = true;
      if (onSeenAll) {
        onSeenAll();
      }
    }
  };

  const onArrowClick = ({ direction }) => {
    if (direction === 'right' && onShowMore) {
      onShowMore();
    }
  };

  return (
    <OrcaCarousel
      slides={inspireDeliveries}
      slidesToShow={SLIDES_TO_SHOW}
      scrollType={SCROLL_TYPE}
      breakpoints={BREAKPOINTS}
      onScroll={onScroll}
      onArrowClick={onArrowClick}
      Component={Card}
      componentProps={{ onImpression: onCardImpression, onHrefClick: onCardHrefClick, cardHrefRouteOptions }}
    />
  );
};
